import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core'
import { GlassSolidIcon } from '@ikyucom/design-system-icons-react'
import { ElementButton, LabelNormal } from 'macaron-frontend-ui'
import { useCallback, useEffect, useRef } from 'react'
import isEqual from 'react-fast-compare'
import { StackableSlideUpModal } from '~/components/modal/StackableSlideUpModal'
import { GetInventoryOnlySeat } from '~/features/guide/getInventoryOnlySeat.graphql'
import { GuideRestaurantInfo as PcGuideRestaurantInfo } from '~/features/guide/pc/GuideRestaurantInfo'
import { GuideRestaurantInfo as SdGuideRestaurantInfo } from '~/features/guide/sd/GuideRestaurantInfo'
import { MEMBER_LIMIT_TYPE } from '~/lib/constants/constants'
import { SystemError } from '~/lib/error/systemError'
import { getFragmentData } from '~/lib/graphql'
import { useSwrGraphqlRequest } from '~/lib/graphql-request/swr'
import { planName } from '~/lib/plan/name'
import { useIsPC } from '~/lib/ua'
import { useReserveNavigate, useReserveOverlay } from '~/navigation'
import { useGuideRestaurant } from '~/navigation/guide'
import { css, sva } from '~/styled-system/css'
import { PlanSelectionInfo } from './PlanSelectionInfo.frag'
import RequestReservationLabel from './RequestReservationLabel'

const seatOnlyModalStyles = sva({
  slots: ['container', 'title', 'content', 'plans', 'information'],
  base: {
    container: {
      py: '4',
      spaceY: '4',
      _pc: {
        py: '6',
        px: '4',
        spaceY: '6',
      },
    },
    title: {
      px: '4',
      fontSize: 'xl',
      fontWeight: 'bold',
      _pc: {
        fontSize: '2xl',
        px: '0',
      },
    },
    content: {
      spaceY: '3',
    },
    plans: {
      px: '4',
      spaceY: '3',
      md: {
        px: '0',
      },
    },
    information: {
      mt: '4',
      borderTop: 'light',
      _empty: {
        visibility: 'hidden',
      },
    },
  },
})

export function SeatOnlyModal() {
  const pc = useIsPC()
  const [context, willBeUnmounted] = useReserveOverlay('SEAT_ONLY_MODAL')
  const { restaurantId, visitDate, visitTime, visitorsCount } = context ?? {}
  const restaurant = useGuideRestaurant()

  const { data, error, isLoading } = useSwrGraphqlRequest(
    GetInventoryOnlySeat,
    {
      restaurantId,
      visitorsCount,
      visitDate,
      visitTime,
    } as VariablesOf<typeof GetInventoryOnlySeat>, // fetch の条件で値が存在することを保証するので強制 cast して OK
    {
      fetch:
        context != undefined &&
        restaurantId != undefined &&
        visitDate != undefined &&
        visitTime != undefined &&
        visitorsCount != undefined,
    }
  )

  const seatOnlyPlans = getFragmentData(
    PlanSelectionInfo,
    data?.restaurant?.inventory.seatOnlyPlans.map((p) => p.plan)
  )

  // seatOnlyPlans が map でレンダリングの度に新しいオブジェクトを作るので deep equal で二度発火しないように
  // useSWR の返す data が毎回新しいオブジェクトになるのか useMemo では回避できない
  // (これまでなぜ大丈夫だったのか謎...)
  const prevSeatOnlyPlans = useRef(seatOnlyPlans)
  const navigate = useReserveNavigate()
  useEffect(() => {
    if (context == undefined) {
      return
    }
    if (seatOnlyPlans == undefined) {
      return
    }
    if (isEqual(prevSeatOnlyPlans.current, seatOnlyPlans)) {
      return
    }
    prevSeatOnlyPlans.current = seatOnlyPlans
    const planIds = seatOnlyPlans.map((p) => p.planId)
    navigate({
      type: 'seatOnlyPlansLoaded',
      planIds,
    })
  }, [context, navigate, seatOnlyPlans])

  // context がないとき何もしない (= SSR されないことも同時に保証している)
  if (context == undefined) {
    return null
  }

  if (error) {
    throw new SystemError(`GetInventoryOnlySeat ${error.message ?? ''}`)
  }

  if (!isLoading && !data?.restaurant) {
    throw new SystemError('GetInventoryOnlySeat: restaurant is undefined')
  }

  const opened = seatOnlyPlans != undefined && seatOnlyPlans.length >= 2
  if (!opened) {
    return null
  }

  const classes = seatOnlyModalStyles()

  return (
    <StackableSlideUpModal
      willBeUnmounted={willBeUnmounted}
      type={pc ? 'modal' : undefined}
      title={pc ? '席の予約' : undefined}
      animationType={pc ? 'fadeIn' : 'up'}
    >
      <div className={classes.container}>
        <div className={classes.content}>
          <h2 className={classes.title}>ご予約内容を選択してください</h2>
          <div className={classes.plans}>
            {(seatOnlyPlans ?? []).map((plan) => (
              <PlanItem key={plan.planId} plan={plan} />
            ))}
          </div>
          <div className={classes.information}>
            {pc ? (
              <div className={css({ pt: '4' })}>
                <PcGuideRestaurantInfo
                  restaurant={context.restaurant ?? restaurant}
                  viewType="planModal"
                  type="modal"
                />
              </div>
            ) : (
              <SdGuideRestaurantInfo
                restaurant={context.restaurant ?? restaurant}
                viewType="planModal"
              />
            )}
          </div>
        </div>
      </div>
    </StackableSlideUpModal>
  )
}

type PlanItemProps = {
  plan: ResultOf<typeof PlanSelectionInfo>
}

const planItemStyles = sva({
  slots: ['container', 'label', 'icon', 'name', 'subLabel', 'button'],
  base: {
    container: {
      p: '3',
      bg: 'gray.100',
      _pc: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        columnGap: '2',
      },
    },
    label: {
      mb: '1',
      display: 'flex',
      flexWrap: 'wrap',
      columnGap: '1',
      _empty: {
        mb: '0',
        visibility: 'hidden',
      },
    },
    icon: {
      mr: '1',
      color: 'gray.700',
    },
    name: {
      fontSize: 'md',
      fontWeight: 'bold',
    },
    subLabel: {
      mt: '1',
      fontSize: 'xs',
      fontWeight: 'bold',
      color: 'gray.700',
      md: {
        fontSize: 'sm',
      },
    },
    button: {
      display: 'flex',
      justifyContent: 'flex-end',
      flexShrink: '0',
    },
  },
})

function PlanItem({ plan }: PlanItemProps) {
  const pc = useIsPC()
  const navigate = useReserveNavigate()
  const onClick = useCallback(() => {
    navigate({
      type: 'reserve',
      planId: plan.planId,
    })
  }, [navigate, plan])

  const classes = planItemStyles()

  return (
    <div className={classes.container}>
      {/* ボタン以外のコンテンツをまとめるために必要なdivです */}
      <div>
        <div className={classes.label}>
          {plan.attributes.includes('FREE_DRINK') && (
            <LabelNormal text="飲み放題">
              <GlassSolidIcon width={12} height={12} className={classes.icon} />
            </LabelNormal>
          )}
          {plan.isRequestReservation && <RequestReservationLabel />}
        </div>
        <p className={classes.name}>{planName(plan)}</p>
        {plan.memberLimitType !== 'NO_LIMIT' && (
          <p className={classes.subLabel}>{MEMBER_LIMIT_TYPE[plan.memberLimitType]}</p>
        )}
      </div>
      <div className={classes.button}>
        <ElementButton
          label="選択"
          size={pc ? 'default' : 'xSmall'}
          color="secondary"
          onClick={onClick}
        />
      </div>
    </div>
  )
}
